import React, {
  lazy, Suspense, useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FileText, X, Sparkles, ChevronRight, ChevronUp, AlertTriangle } from 'lucide-react';
import { BypassLayout, Button, Modal } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { ProblemNavGuard } from '../components/ProblemHubV2/ProblemIdLink';
import PaperListPanel from '../components/ExamPapersV2/PaperListPanel';
import PaperItemsEditor from '../components/ExamPapersV2/PaperItemsEditor';
import PaperComposeModal from '../components/ExamPapersV2/PaperComposeModal';
import FolderTree, { FolderPickerInline } from '../components/ExamPapersV2/FolderTree';
import RevisionPanel from '../components/ExamPapersV2/RevisionPanel';
import AssignPanel from '../components/ExamPapersV2/AssignPanel';
// 채점기(AssignmentPanel)는 이 페이지가 직접 그리지 않는다 — 창이 안에서 감싼다
import AssignmentGradeModal from '../components/ExamPapersV2/AssignmentGradeModal';
import AiCopyModal from '../components/ExamPapersV2/AiCopyModal';
import BatchPanel from '../components/ExamPapersV2/BatchPanel';
import {
  fetchPaper, movePapers, copyPaper, deletePaper,
  describeApiError, deletedItemCount, paperPrintDataOf,
  UNFILED, AUTH_REDIRECT,
} from '../components/ExamPapersV2/examPaperApi';
import { paperHeadMeta } from '../components/ExamPapersV2/PaperTable';
import { AI_RUN_ROLES } from '../utils/roles';
import { FOLDER_WIDTH, clampFolderWidth, readFolderWidth, writeFolderWidth } from '../components/ExamPapersV2/folderPaneWidth';

// 최신 인쇄(v4, A4/B4)는 Lexical·KaTeX·조판 코드가 무거워 목록에서 실제로 누를 때만 받는다.
const PrintPreviewModal = lazy(() => import('../components/printPreview').then((m) => ({
  default: m.PrintPreviewModal,
})));

/**
 * 「시험지」 (/exam-papers) — 신규 시험지(⑦) + AI 복사 시험지.
 * 계약: BE-20260825-exam-papers-screens.md
 *
 * 좌(마스터): 시험지 목록 — [새 시험지] · 행마다 [AI 복사]
 * 우(디테일): 경로가 무엇을 띄울지 정한다
 *   /paper/:id?tab=  시험지 상세 — 탭 [문항 편집 items] [학생 출제 assign] [변경 이력 history].
 *                   **탭은 주소가 정본이다** — 새로고침·뒤로/앞으로가 그대로 먹는다.
 *   /new            새 시험지 편집(저장 전까지 서버에 없음)
 *   /assignment/:id 배포본 채점·마감
 *   /batch/:id      AI 복사 배치 — 문항별 검수 후 조립
 *
 * AI 복사 배치는 문항 수만큼 변형이 도는 긴 작업이라 URL 로 열어 둔다 — 모달이면 실수로 닫는
 * 순간 흐름이 끊긴다. (되찾는 길 자체는 배치 목록 API 가 생겨(contract-answers §1-2) 목록 위
 * [진행하던 배치] 줄로도 열린다.)
 *
 * 좌측은 폴더 트리 + 시험지 목록이다. 폴더는 시험지를 0~1개 담는 정식 소유 폴더라(folders §1),
 * "전체 / 무소속 / 각 폴더" 셋으로 목록의 모집단이 갈린다.
 *
 * 헤더·사이드바는 페이지가 스스로 두른다(BypassLayout) — 이 저장소 관례. 마스터-디테일
 * 치수는 형제 페이지(ProblemHub·OcrHub)와 같은 규칙.
 */

const TABS = [
  ['items', '문항 편집'],
  ['assign', '학생 출제'],
  ['history', '변경 이력'],
];
/** 주소(`?tab=`)로 들어온 값을 거른다 — 모르는 값이면 [문항 편집]으로 떨어진다 */
const TAB_KEYS = TABS.map(([key]) => key);

/** 학생 허브에서 건넨 복귀 주소만 허용한다. route state 는 외부 입력과 같아 그대로 이동하지 않는다. */
const assignmentBackPath = (returnTo) => (
  typeof returnTo === 'string'
    && (returnTo === '/students-hub' || /^\/students-hub\/student\/\d+$/.test(returnTo))
    ? returnTo
    : '/exam-papers'
);

function ExamPapers() {
  const { paperId, assignmentId, batchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessToken, userType, userId } = useAuth();
  /**
   * AI 복사(변형)를 쓸 수 있는가 — 매니저만(오너 지시 2026-09-09, 값은 utils/roles.js).
   * 이 값이 거짓이면 아래 두 콜백을 **넘기지 않는다**. 그러면 목록 행의 [AI변형] 버튼과
   * 머리의 「진행중인 AI변형 시험지」 줄이 함께 사라진다 — 셋 다 콜백 유무로 그릴지 정하기 때문이다.
   * 부품에 조건을 심지 않는 이유: 같은 판정이 세 파일에 흩어지면 한 곳만 고쳐지는 날이 온다.
   */
  const canAiCopy = AI_RUN_ROLES.includes(userType);

  const isNew = location.pathname.endsWith('/exam-papers/new');
  /**
   * 지금 탭 — **주소가 정본이다.** state 를 따로 두지 않으므로 새로고침·뒤로가기·앞으로가기가
   * 손댈 것 없이 그대로 먹는다(오너 요구 2026-09-05).
   *
   * 전에는 `?tab=` 이 **진입 탭만** 정하고 탭 버튼은 state 만 바꿨다. 그래서 [학생 출제]를 보다가
   * 새로고침하면 [문항 편집]으로 떨어졌고, 주소를 복사해 건네도 상대는 다른 탭을 봤다.
   * 값이 둘(주소·state)이면 어느 쪽이 이기는지를 effect 로 조정해야 했는데, 그 조정 자체가
   * 사라진다 — 탭 버튼이 하는 일은 이제 주소를 바꾸는 것뿐이다(goTab).
   *
   * 모르는 값(`?tab=zzz`)은 [문항 편집]으로 읽고, 아래 정규화 effect 가 주소까지 고쳐 놓는다.
   */
  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedTab = search.get('tab');
  const tab = TAB_KEYS.includes(requestedTab) ? requestedTab : 'items';
  /**
   * 결과표에서 연 채점 창 — **모달도 주소가 쥔다**(오너 결정 2026-09-06 D1).
   * 탭을 주소 정본으로 바꿔 놓고 모달만 주소 밖에 두면 같은 화면에서 규칙이 둘로 갈린다.
   * 정수만 받는다 — 주소는 사용자가 손으로 고칠 수 있는 자리라, 모르는 값이면 열지 않는다.
   */
  const requestedGrade = search.get('grade');
  const gradeId = /^\d+$/.test(String(requestedGrade ?? '')) ? requestedGrade : null;
  /**
   * 지금 주소의 쿼리를 **보존하며** 일부만 갈아 끼운다.
   * 문자열로 `?tab=items` 를 새로 지으면 그때마다 grade 가 조용히 사라진다 — 실제로 아래 정규화
   * 효과와 goTab 이 그렇게 짜여 있었고, tab 이 없는 주소로 들어오면 채점 창이 열리자마자 닫혔다.
   */
  const paperPath = useCallback((patch) => {
    const next = new URLSearchParams(location.search);
    Object.entries(patch).forEach(([k, v]) => {
      if (v == null) next.delete(k);
      else next.set(k, String(v));
    });
    const qs = next.toString();
    return `/exam-papers/paper/${paperId}${qs ? `?${qs}` : ''}`;
  }, [location.search, paperId]);
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  /**
   * 배포 목록만 다시 읽게 하는 토큰. 채점 창에서 저장·마감하면 뒤에 깔린 결과표의 숫자(정답·오답·
   * 상태·마감일)가 낡는데, 시험지 전체를 다시 읽을(bumpRefresh) 이유는 없다 — 문항이 바뀐 것이
   * 아니라 배정이 바뀐 것이다.
   */
  const [assignRefresh, setAssignRefresh] = useState(0);
  const bumpAssignRefresh = useCallback(() => setAssignRefresh((k) => k + 1), []);

  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(false); // 헤더의 '읽는 중' 표시
  const [error, setError] = useState(null);
  const seqRef = useRef(0);

  // 미저장 여부 — 문항 편집·채점·검수 세 곳이 모두 여기에 모인다. 시험지를 옮기거나 닫으면
  // 그 패널이 언마운트되며 편집이 사라지기 때문이다.
  // ref 와 state 를 함께 두는 이유: 가드는 렌더 밖에서도 최신값을 봐야 하고(ref),
  // 배포 확인 바는 "저장 안 한 문항 변경은 스냅샷에 안 담긴다"를 렌더로 말해야 한다(state).
  const dirtyRef = useRef(false);
  const [paperDirty, setPaperDirty] = useState(false);
  const markDirty = useCallback((d) => { dirtyRef.current = d; setPaperDirty(d); }, []);
  // 플래그를 여기서 끄지 않는다 — 끄고 나서 실제로 언마운트되지 않는 경로(AI 복사 대화상자 취소,
  // 되돌리기 실패)가 있으면 편집은 살아 있는데 가드만 풀려, 다음 이동에서 조용히 사라진다.
  // 해제는 패널이 언마운트되며 스스로 알린다(onDirtyChange(false)).
  // 참조를 고정한다 — 이 함수는 화면 전체에 <ProblemNavGuard> 로 내려가고, 매 렌더마다 새
  // 함수면 그것을 읽는 문제 번호 링크 전부(문항 200개짜리 시험지면 200개)가 함께 다시 그려진다.
  const confirmDiscard = useCallback(() => {
    if (!dirtyRef.current) return true;
    // eslint-disable-next-line no-alert
    return window.confirm('저장하지 않은 변경 사항이 있습니다. 버리고 이동할까요?');
  }, []);
  useEffect(() => {
    const onBeforeUnload = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const loadPaper = useCallback(async () => {
    // 순번을 먼저 올린다 — 닫기·전환 때 일찍 빠져나가면 앞선 요청이 살아남아, 닫힌 패널이나
    // 다른 시험지 자리에 옛 응답이 들어앉는다.
    const seq = (seqRef.current += 1);
    if (paperId == null) { setPaper(null); setError(null); setLoading(false); return; }
    // 다른 시험지로 옮길 때만 비운다. 같은 시험지를 다시 읽는 것(저장·되돌리기 후 갱신)까지
    // 비우면 화면이 스켈레톤으로 갈아 끼워지며 방금 뜬 "저장했습니다"가 함께 지워진다.
    setPaper((prev) => (prev && String(prev.paper_id ?? prev.id) === String(paperId) ? prev : null));
    setLoading(true);
    setError(null);
    try {
      const d = await fetchPaper(paperId, getAccessToken);
      if (seq !== seqRef.current) return;
      // 봉투는 성공인데 data 가 비어 오면 빈 흰 카드만 남는다 — 오류로 다룬다
      if (d == null) throw new Error('시험지 내용을 받지 못했습니다.');
      setPaper(d);
    } catch (e) {
      if (seq !== seqRef.current) return;
      setError(e.message || '시험지를 불러오지 못했습니다.');
      setPaper(null);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [paperId, getAccessToken]);

  useEffect(() => { loadPaper(); }, [loadPaper, refreshKey]);
  /**
   * 주소에 탭이 없거나 모르는 값이면 [문항 편집]으로 못박는다. 화면은 이미 그렇게 그리는데
   * 주소만 비어 있으면, 그 주소를 복사해 간 사람이 무엇을 보게 될지 알 수 없다 — 셋 중 하나만
   * 주소에 안 적히면 「아직 안 정했다」와 「문항 편집이다」가 구분되지 않는다.
   *
   * replace 로 바꾼다 — 뒤로가기가 이 정규화 한 칸에 걸려 목록으로 못 돌아가면 안 된다.
   * 시험지를 바꿔도 [문항 편집]부터 열리는 종전 동작은 그대로다: 목록이 `/paper/:id` 로만
   * 열므로(select) 여기서 `?tab=items` 가 붙는다. 「학생 출제」 아이콘만 예외로 탭을 지정해 연다.
   */
  useEffect(() => {
    if (paperId == null || isNew) return;
    if (TAB_KEYS.includes(requestedTab)) return;
    navigate(paperPath({ tab: 'items' }), { replace: true });
  }, [paperId, isNew, requestedTab, navigate, paperPath]);
  // 무엇을 열든 목록은 접힌다 — 펼친 채로 다음 시험지를 고르면 아래가 열린 줄 모른다.
  // 목록으로 돌아오면 다시 펴 둔다(다음 진입이 접힌 목록으로 시작하지 않게).
  useEffect(() => {
    setListCollapsed(true);
  }, [paperId, assignmentId, batchId, isNew]);
  // /exam-papers/new 로 들어오면 제작 창을 바로 연다(딥링크·[새 시험지] 둘 다 같은 자리로)
  useEffect(() => { if (isNew) setComposeFor((c) => c || 'new'); }, [isNew]);

  const [aiCopyTarget, setAiCopyTarget] = useState(null); // AI 복사 대화상자 대상
  const [composeFor, setComposeFor] = useState(null); // 3열 제작 모달: 'new' | 시험지 객체
  // 목록을 접었는가 — 상하 분할에서 위(목록)와 아래(상세)가 높이를 나눠 갖는 축이다.
  // 상세가 열리면 자동으로 접히고, 목록 머리줄을 눌러 다시 편다(그때 아래는 0으로 줄어든다).
  const [listCollapsed, setListCollapsed] = useState(true);
  // 접혔을 때 남길 높이 = 목록 머리줄 한 줄. 목록이 실측해 올려 준다(글자·버튼이 바뀌면 따라 움직인다).
  // 초기값은 첫 렌더 한 프레임에만 쓰이는 어림치다.
  const [headH, setHeadH] = useState(46);

  /**
   * 목록 펼침/접힘 토글 — 펼칠 때(표로 돌아갈 때) **보고 있던 시험지가 그 표에 있도록**
   * 폴더 선택을 함께 옮긴다.
   *
   * 안 옮기면 이런 일이 난다: 폴더 A 를 보다가 시험지를 열고, 트리에서 빈 폴더 B 를 눌렀다가,
   * 다시 펼치면 **B 의 빈 표**가 나온다 — 방금까지 보던 시험지가 어디에도 없다(오너 지적).
   *
   * 「전체」(null)를 보고 있을 때는 건드리지 않는다. 거기엔 모든 시험지가 있으므로
   * 굳이 모집단을 좁힐 이유가 없다.
   */
  const toggleList = () => {
    const next = !listCollapsed;
    if (!next && paper != null) {
      const pf = paper.folder_id ?? null;
      const target = pf == null ? UNFILED : pf;
      if (folderId !== null && String(folderId) !== String(target)) setFolderId(target);
    }
    setListCollapsed(next);
  };
  const onHeaderHeight = useCallback((h) => {
    setHeadH((prev) => (Math.abs(prev - h) < 0.5 ? prev : h));
  }, []);
  const [folderId, setFolderId] = useState(null);     // null 전체 | 'none' 무소속 | 정수
  /**
   * 폴더 칸의 폭 — 오른쪽 끝을 끌어 바꾸고, 놓을 때 세션에 남긴다(folderPaneWidth.js).
   * 읽기는 첫 렌더에 한 번(지연 초기화)이고, 계정이 바뀌면 그 계정 값으로 다시 읽는다.
   * 끄는 동안의 값은 ref 에도 둔다 — 놓는 순간 저장할 값이 마지막 렌더를 기다리지 않게.
   */
  const [folderWidth, setFolderWidth] = useState(() => readFolderWidth(userId));
  const [folderResizing, setFolderResizing] = useState(false);
  const folderDragRef = useRef(null); // { startX, startW, last } — 끄는 중에만 값이 있다
  useEffect(() => { setFolderWidth(readFolderWidth(userId)); }, [userId]);
  /**
   * 고른 폴더의 **자손 폴더까지** 함께 볼 것인가 — 계약 §2 의 include_children.
   * 폴더가 폴더를 담게 되면서(깊이 최대 3) 생긴 축이다. 부모 폴더를 눌렀을 때 직접 소속만
   * 보이면, 시험지를 자식 폴더에 넣어 둔 사람 눈에는 그 폴더가 빈 것으로 보인다.
   * 값은 트리가 정해 올린다(펼침 상태·모드에 따라 다르므로 트리 쪽이 사실을 안다).
   */
  const [includeChildren, setIncludeChildren] = useState(false);
  const [folders, setFolders] = useState([]);         // 트리가 읽은 목록을 그대로 받아 쓴다

  /**
   * 「하위 폴더 포함」 스위치가 설 조건 — 고른 것이 **폴더**이고 그 안에 하위가 있을 때만이다.
   * 전체·무소속에는 자손이라는 개념이 없다. 값은 트리가 올려 준 평면 목록에서 센다 —
   * 부모가 트리를 또 만들면 두 화면의 셈이 갈릴 수 있다.
   */
  const folderScope = useMemo(() => {
    if (folderId == null || folderId === UNFILED) return { kidCount: 0, totalCount: null };
    const same = (a, b) => a != null && b != null && String(a) === String(b);
    const node = folders.find((f) => same(f.folder_id ?? f.id, folderId));
    const kidCount = folders.filter((f) => same(f.parent_id, folderId)).length;
    const n = Number(node?.total_paper_count);
    return { kidCount, totalCount: Number.isFinite(n) ? n : null };
  }, [folders, folderId]);

  // 마지막 하위 폴더가 옮겨 가거나 지워지면 스위치가 화면에서 사라진다 — 켜진 값이 남으면
  // 보이지 않는 조건이 목록을 계속 거른다.
  useEffect(() => {
    if (folderScope.kidCount === 0) setIncludeChildren(false);
  }, [folderScope.kidCount]);
  const [rowAction, setRowAction] = useState(null);   // {kind:'copy'|'delete', paper, folderId?, title?}
  const [rowBusy, setRowBusy] = useState(false);
  const [rowToast, setRowToast] = useState(null);
  // 목록 [미리보기] — 상세를 읽는 동안의 행 id와, 검증이 끝나 모달에 넘길 고정 입력.
  const [previewBusyId, setPreviewBusyId] = useState(null);
  const [printData, setPrintData] = useState(null);
  const previewBusyRef = useRef(false); // 같은 프레임의 연속 클릭까지 막는다(state만으로는 한 박자 늦다)
  const previewSeqRef = useRef(0);      // 늦게 도착한 옛 상세가 새 화면 위에 미리보기를 열지 않게
  /**
   * 화면 아래 알림 띠 — 이동·복사·삭제의 **성공**을 여기서만 말한다.
   *
   * 대화상자 안(rowToast)에 두면 성공 메시지는 눈에 닿지 못한다. 성공하면 같은 흐름에서
   * 대화상자를 닫는데, React 18 은 두 상태 변경을 한 번의 그림으로 묶어서(자동 배칭)
   * **메시지를 그릴 자리가 그 그림에서 이미 사라져 있다** — 한 프레임도 안 뜬다.
   * 그래서 성공은 대화상자 밖(이 띠)으로 내보내고, 실패만 대화상자 안에 남긴다
   * (실패하면 대화상자가 열린 채라 그 자리가 살아 있다).
   */
  const [flash, setFlash] = useState(null);
  const flashTimer = useRef(null);
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);
  const say = useCallback((t) => {
    setFlash(t);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    // 오류는 스스로 사라지지 않는다 — 못 했다는 말이 눈을 스쳐 지나가면 된 줄 안다
    if (t?.type !== 'error') flashTimer.current = setTimeout(() => setFlash(null), 3200);
  }, []);

  /**
   * 목록 표·모바일 카드의 [미리보기] — **저장된 최신 판**을 다시 읽어 공용 v4 인쇄 모달로 보낸다.
   * 목록 응답은 문항 수만 있고 id 배열은 없으므로 상세 GET 한 번이 반드시 필요하다.
   * 같은 시험지에 미저장 편집이 있으면 서버 판을 인쇄하는 것이므로 조용히 열지 않는다.
   */
  const openPaperPreview = useCallback(async (target) => {
    const id = target?.paper_id ?? target?.id;
    if (id == null || previewBusyRef.current) return;

    if (paperDirty && paperId != null && String(id) === String(paperId)) {
      say({
        type: 'error',
        text: '저장하지 않은 문항 변경이 있습니다 — 저장한 뒤 미리보기를 열어 주세요.',
      });
      return;
    }

    previewBusyRef.current = true;
    const seq = (previewSeqRef.current += 1);
    setPreviewBusyId(id);
    setPrintData(null);
    setFlash(null);
    if (flashTimer.current) {
      clearTimeout(flashTimer.current);
      flashTimer.current = null;
    }

    try {
      const detail = await fetchPaper(id, getAccessToken);
      if (seq !== previewSeqRef.current) return;
      setPrintData(paperPrintDataOf(detail, target?.title || ''));
    } catch (e) {
      if (seq !== previewSeqRef.current) return;
      say({ type: 'error', text: e?.message || '시험지 미리보기를 준비하지 못했습니다.' });
    } finally {
      if (seq === previewSeqRef.current) {
        previewBusyRef.current = false;
        setPreviewBusyId(null);
      }
    }
  }, [getAccessToken, paperDirty, paperId, say]);

  // 상세 조회 도중 다른 화면으로 옮기면 그 응답은 더 이상 현재 사용자의 요청이 아니다.
  useEffect(() => {
    previewSeqRef.current += 1;
    previewBusyRef.current = false;
    setPreviewBusyId(null);
    setPrintData(null);
    return () => {
      // 이 페이지 자체가 사라질 때도 진행 중인 응답을 무효화한다. 그렇지 않으면 늦은 응답이
      // 언마운트된 화면의 state를 다시 세우려 하고, 다음 진입의 미리보기와 경계가 흐려진다.
      previewSeqRef.current += 1;
      previewBusyRef.current = false;
    };
  }, [location.pathname]);

  const closeTimerRef = useRef(null);

  // 예약된 닫기 취소 — 닫히는 동안 다른 곳으로 옮겨 가면 그 예약은 무효다.
  // 안 지우면 320ms 뒤에 방금 연 시험지에서 목록으로 튕긴다.
  const cancelClose = () => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
  };
  const go = (path) => {
    if (!confirmDiscard()) return false;
    cancelClose();
    navigate(path);
    return true;
  };
  /**
   * 표에서 행을 고른다. **이미 열려 있는 시험지를 다시 누르면 상세를 편다.**
   *
   * 그 경우 라우트가 그대로라 paperId 가 안 바뀌고, 접힘을 맡는 effect(=시험지를 바꾸면
   * 상세부터)가 돌지 않는다. 그래서 목록을 펴 둔 채 그 행을 눌러도 아무 일이 없었다 —
   * 표에서 상세로 돌아가는 길이 아래 손잡이 하나뿐이었다는 뜻이다.
   */
  const select = (id) => {
    if (paperId != null && String(id) === String(paperId)) {
      setListCollapsed(true); // 같은 시험지다 — 버릴 편집이 없으니 가드를 지나지 않는다
      return;
    }
    go(`/exam-papers/paper/${id}`);
  };

  /**
   * 탭을 옮긴다 — **주소를 바꾸는 것이 곧 탭을 바꾸는 것**이다(위 tab 파생값).
   *
   * push 로 쌓는다: 뒤로가기로 직전 탭에 돌아오는 것이 오너가 요구한 동작이다.
   * 미저장 가드(go·confirmDiscard)를 지나지 않는다 — 탭은 패널을 언마운트하지 않고 hidden 으로
   * 숨길 뿐이라 버릴 편집이 없다. 여기서 물으면 헛되이 뜨는 확인창이 된다.
   * 같은 탭을 다시 누르면 아무것도 하지 않는다 — 같은 주소가 겹쳐 쌓이면 뒤로가기를 두 번
   * 눌러야 직전 탭에 닿는다.
   */
  const goTab = (key) => {
    if (paperId == null || key === tab) return;
    // 탭을 옮기면 채점 창은 닫는다 — 다른 탭 위에 남의 배포본 창이 떠 있을 이유가 없다
    navigate(paperPath({ tab: key, grade: null }));
  };

  /**
   * 결과표의 [결과] → 채점 창을 연다. push 로 쌓아 **뒤로가기로 창만 닫히게** 한다.
   * 미저장 가드(go)를 지나지 않는다 — 이 클릭은 무엇도 언마운트하지 않고 창 하나를 얹을 뿐이다.
   */
  const gradeOpenedByUsRef = useRef(false);
  const openGrade = (aid) => {
    if (aid == null || paperId == null) return;
    gradeOpenedByUsRef.current = true;
    navigate(paperPath({ tab: 'assign', grade: aid }));
  };
  /**
   * 창을 닫는다. **우리가 열었을 때만 뒤로 한 칸**이다 — 그래야 히스토리에 같은 주소가 겹쳐 쌓여
   * 뒤로가기가 헛돌지 않는다. 딥링크나 새로고침으로 창이 떠 있는 경우에는 뒤 칸이 이 앱이 아닐 수
   * 있어(브라우저 히스토리가 초기화된다) 뒤로 가면 앱 밖으로 나간다 — 그때는 주소만 바꿔 닫는다.
   */
  const closeGrade = () => {
    if (gradeOpenedByUsRef.current) {
      gradeOpenedByUsRef.current = false;
      navigate(-1);
      return;
    }
    navigate(paperPath({ grade: null }), { replace: true });
  };
  // 창이 닫히면(뒤로가기로 닫힌 경우 포함) 표식을 내린다 — 다음 닫기가 엉뚱한 칸으로 가지 않게
  useEffect(() => { if (gradeId == null) gradeOpenedByUsRef.current = false; }, [gradeId]);

  /**
   * 채점은 **어느 길로 들어와도 창으로 연다**(오너 지시 2026-09-06). 두 길이 있다:
   *   결과표의 [결과]            `?grade=<aid>` — 그 시험지의 결과표를 배경에 두고 뜬다
   *   학생 학습관리의 배정 카드   `/exam-papers/assignment/<aid>` — 시험지 목록을 배경에 두고 뜬다
   *
   * 처음에는 뒤엣것을 전체 화면으로 두려 했다. 그 화면에는 배경에 깔 결과표가 없기 때문인데,
   * 같은 일을 하는 화면이 둘이면 고칠 곳도 둘이 된다. 배경이 목록이어도 창은 뜬다.
   */
  const openGradeId = gradeId ?? assignmentId;
  const closeGradeModal = () => {
    if (gradeId != null) { closeGrade(); return; }
    // 학생 허브에서 온 링크만 그 학생 화면으로 돌아간다. 딥링크·새로고침·그 밖의 state 는
    // 앱 밖으로 나가지 않도록 시험지 목록으로 명시해 보낸다.
    leaveDetail(assignmentBackPath(location.state?.returnTo));
  };
  /** 채점이 서버에 남으면 뒤에 깔린 것이 낡는다 — 어느 배경이 깔렸든 둘 다 새로 읽게 한다 */
  const handleGraded = () => { bumpRefresh(); bumpAssignRefresh(); };

  /**
   * 목록에서 「학생 출제」 아이콘을 누른다 — 그 시험지의 [학생 출제] 탭을 편다.
   *
   * **이미 열려 있는 시험지면 가드를 지나지 않는다.** select() 와 같은 이유로 버릴 편집이
   * 없어(패널이 언마운트되지 않는다) 지날 까닭이 없고, 지나면 확인창만 헛되이 뜬다.
   * 그때는 탭만 옮기면 되고, 이미 [학생 출제]를 보고 있으면 goTab 이 아무것도 하지 않는다.
   */
  const openAssign = (id) => {
    const same = paperId != null && String(id) === String(paperId);
    if (same) goTab('assign');
    else if (!go(`/exam-papers/paper/${id}?tab=assign`)) return;
    setListCollapsed(true);
  };

  /**
   * 진행하던 배치 줄을 누른다. **이미 보고 있는 배치를 다시 눌러도 상세를 편다** —
   * select() 와 같은 이유다. 그 경우 라우트가 그대로라 batchId 가 안 바뀌고, 접힘을 맡는
   * effect 가 돌지 않아 목록을 펴 둔 채로는 눌러도 아무 일이 없었다(오너 지적).
   */
  // 배치 폐기 응답이 왔을 때 아직 그 배치를 보고 있는지 판단한다 — 콜백은 누른 순간의 렌더에 묶여 있다
  const batchIdRef = useRef(batchId);
  batchIdRef.current = batchId;
  const openBatch = (id) => {
    if (batchId != null && String(id) === String(batchId)) {
      setListCollapsed(true); // 같은 배치다 — 패널이 언마운트되지 않으니 버릴 편집이 없다
      return;
    }
    go(`/exam-papers/batch/${id}`);
  };

  /**
   * 상세를 떠나는 길 — 열 때와 같은 애니메이션을 거꾸로 한 번 태우고 나간다.
   *
   * 바로 navigate 하면 아래 칸이 그 프레임에 언마운트돼, 목록만 늘어나고 상세는 툭 사라진다.
   * 그래서 먼저 목록을 펴서(= 아래를 0 으로 줄여서) 접히는 것을 보여 준 뒤 주소를 바꾼다.
   * 라우트가 그대로인 동안 상세는 자기 내용을 그대로 들고 줄어든다 — 사라지는 도중에
   * 스켈레톤으로 갈아 끼워지는 깜빡임이 없다.
   */
  const leaveDetail = (path = '/exam-papers') => {
    if (closeTimerRef.current) return; // 이미 닫는 중 — 두 번 눌러도 타이머를 늘리지 않는다
    if (!confirmDiscard()) return;
    // 움직임을 줄여 달라고 한 사람에게 320ms 는 애니메이션이 아니라 그냥 지연이다
    const reduce = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !detailOpen) { navigate(path); return; }
    setListCollapsed(false);
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      navigate(path);
    }, 320);
  };
  const closeDetail = () => leaveDetail('/exam-papers');

  // 언마운트 정리 + 경로가 먼저 바뀌면 예약된 닫기를 버린다.
  // go() 를 거치지 않고 navigate 를 직접 부르는 길(삭제 후·조립 후)도 여기서 함께 걸린다.
  useEffect(() => {
    cancelClose();
    return () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 의도: 경로가 바뀔 때만 버린다
  }, [location.pathname]);

  /**
   * 상세 칸(오른쪽/아래)을 여는가. **배정은 여기 들어오지 않는다** — 채점은 창으로 뜨고
   * 그 뒤에는 목록이 그대로 남아야 한다(2026-09-06). 배정 주소로 들어오면 목록 + 창이 된다.
   */
  const detailOpen = isNew || paperId != null || batchId != null;

  /**
   * 이 시험지에 든 **삭제된 문항** 수(계약 §1-3 — 상세에 items[].is_deleted 와 deleted_item_count 가 온다).
   *
   * 0 이 아니면 배포·복사·되돌리기가 전부 409 로 막힌다. 그래서 눌러 보고 막히기 전에
   * 머리줄에서 먼저 말한다 — 「왜 배포 버튼을 눌렀는데 거절당했나」를 되짚게 두지 않는다.
   * 목록 행 요약에는 이 값이 없어 상세를 연 뒤에만 알 수 있다.
   * 옛 시험지가 남아 있는 프레임에서 남의 숫자를 띄우지 않도록 id 가 맞을 때만 센다.
   */
  const paperIsMine = paper != null && String(paper.paper_id ?? paper.id ?? paperId) === String(paperId);
  const deletedItems = paperId != null && paperIsMine ? deletedItemCount(paper) : 0;

  /**
   * 폴더 이동 — 표의 [이동] 미니 모달에서 폴더를 고른 즉시 부른다. 확인 단계가 없다.
   *
   * 확인을 뺀 근거: 이동은 **소속만 바꾸고 되돌리기가 같은 조작 한 번**이다(다른 폴더를 다시
   * 고르면 끝). 복사·삭제와 달리 잃는 것이 없어, 물어보는 비용이 되돌리는 비용보다 크다.
   *
   * 폴더로 거른 목록에서 옮기면 그 행이 목록에서 사라진다 — 그래서 조용히 끝내지 않고
   * 어디로 갔는지 이름을 넣어 말한다.
   */
  const moveTo = async (paper, targetFolderId) => {
    const id = paper.paper_id ?? paper.id;
    const where = targetFolderId == null
      ? null
      : folders.find((f) => String(f.folder_id ?? f.id) === String(targetFolderId));
    try {
      const d = await movePapers([Number(id)], targetFolderId ?? null, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      say({
        type: 'info',
        text: targetFolderId == null
          ? '폴더에서 뺐습니다.'
          : `'${where?.name || `#${targetFolderId}`}' 폴더로 옮겼습니다.`,
      });
      bumpRefresh();
    } catch (e) {
      say({ type: 'error', text: describeApiError(e, '옮기지 못했습니다.') });
    }
  };

  /**
   * 행 액션(복사·삭제) — 확인은 인라인 바로 받는다. 둘은 결과가 다르다:
   * 복사는 원본과 연결되지 않은 새 시험지를 만들고(folders §3), 삭제는 진짜로 없앤다.
   * 그 차이를 확인 바가 말해야 한다. (이동은 위 moveTo 로 빠졌다 — 확인이 필요 없다.)
   *
   * 둘 다 **막힐 수 있다**(409). 복사는 삭제된 문항이 들어 있을 때(§1-3 deleted_items),
   * 삭제는 배정이 하나라도 있을 때(§10 has_assignments). 사유는 봉투 data.reason 으로 오므로
   * 문자열을 보지 않고 describeApiError 에 넘겨 문구를 만든다 — 서버 문구가 바뀌어도 화면은 그대로다.
   */
  const runRowAction = async () => {
    if (!rowAction || rowBusy) return;
    const { kind, paper: target } = rowAction;
    const id = target.paper_id ?? target.id;
    setRowBusy(true);
    setRowToast(null);
    try {
      if (kind === 'copy') {
        const d = await copyPaper(id, { folderId: rowAction.folderId, title: rowAction.title }, getAccessToken);
        if (d === AUTH_REDIRECT) return;
        const newId = d?.paper_id ?? d?.id;
        say({
          type: 'info',
          text: newId != null ? `사본을 만들었습니다 — 새 시험지 #${newId}` : '사본을 만들었습니다.',
        });
      } else {
        const d = await deletePaper(id, getAccessToken);
        if (d === AUTH_REDIRECT) return;
        say({ type: 'info', text: '시험지를 삭제했습니다.' });
        if (String(id) === String(paperId)) navigate('/exam-papers');
      }
      setRowAction(null);
      bumpRefresh();
    } catch (e) {
      setRowToast({
        type: 'error',
        text: describeApiError(e, kind === 'copy' ? '복사하지 못했습니다.' : '삭제하지 못했습니다.'),
      });
    } finally {
      setRowBusy(false);
    }
  };

  /**
   * 폴더 칸 폭 손잡이 — 칸 오른쪽 끝(옆 카드와의 틈 12px)에 선다.
   * 포인터를 잡아 두고(setPointerCapture) 끄는 동안 폭만 바꾸며, **놓을 때 한 번** 세션에 쓴다 —
   * 매 이동마다 쓰면 초당 수십 번 저장소를 두드린다. 키보드(← → Home End)로도 움직이고,
   * 두 번 누르면 처음 폭으로 돌아간다(끌다가 너무 넓혔을 때 되돌리는 길).
   */
  const commitFolderWidth = (w) => {
    const next = clampFolderWidth(w);
    setFolderWidth(next);
    writeFolderWidth(userId, next);
  };
  const onFolderResizeDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault(); // 끄는 동안 옆 글자가 선택되지 않게
    e.currentTarget.setPointerCapture?.(e.pointerId);
    folderDragRef.current = { startX: e.clientX, startW: folderWidth, last: null };
    setFolderResizing(true);
  };
  const onFolderResizeMove = (e) => {
    const d = folderDragRef.current;
    if (!d) return;
    const w = clampFolderWidth(d.startW + (e.clientX - d.startX));
    d.last = w;
    setFolderWidth(w);
  };
  const onFolderResizeEnd = (e) => {
    const d = folderDragRef.current;
    if (!d) return;
    folderDragRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setFolderResizing(false);
    // 누르기만 하고 움직이지 않았으면 쓸 것이 없다
    if (d.last != null) writeFolderWidth(userId, d.last);
  };
  const onFolderResizeKey = (e) => {
    const moves = {
      ArrowLeft: folderWidth - FOLDER_WIDTH.step,
      ArrowRight: folderWidth + FOLDER_WIDTH.step,
      Home: FOLDER_WIDTH.min,
      End: FOLDER_WIDTH.max,
    };
    if (!(e.key in moves)) return;
    e.preventDefault();
    commitFolderWidth(moves[e.key]);
  };

  /**
   * 폴더는 목록의 모집단을 가르는 축이라 넓은 폭에서는 왼쪽에 세로로 상시 둔다.
   * 위아래로 나뉘는 것은 이 옆의 두 칸(시험지 목록 · 상세)이고, 폴더는 그 분할 밖에서
   * 세로를 통째로 쓴다 — 트리가 함께 짧아지면 폴더가 서너 개만 보인다.
   * 폭은 사용자가 정한다(위 손잡이) — 기본은 옛 고정 폭 224px 그대로다.
   */
  const renderFolders = () => (
    <div className="relative h-full shrink-0" style={{ width: folderWidth }}>
      {/* 손잡이 — 칸과 옆 카드 사이의 틈(gap-3 = 12px)에 정확히 겹친다. 평소에는 안 보이고
          마우스를 올리거나 초점이 오면 세로 막대가 뜬다. 끄는 중에는 프로젝트 색으로 붙어 있는다. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="폴더 칸 폭"
        aria-valuemin={FOLDER_WIDTH.min}
        aria-valuemax={FOLDER_WIDTH.max}
        aria-valuenow={folderWidth}
        tabIndex={0}
        title="끌어서 폭 조절 · 두 번 눌러 원래대로"
        onPointerDown={onFolderResizeDown}
        onPointerMove={onFolderResizeMove}
        onPointerUp={onFolderResizeEnd}
        onPointerCancel={onFolderResizeEnd}
        onKeyDown={onFolderResizeKey}
        onDoubleClick={() => commitFolderWidth(FOLDER_WIDTH.initial)}
        className="group absolute inset-y-0 -right-3 z-10 flex w-3 cursor-col-resize touch-none items-center justify-center focus:outline-none"
      >
        <span
          aria-hidden="true"
          className={`h-10 w-1 rounded-full transition-colors ${
            folderResizing ? 'bg-primary' : 'bg-transparent group-hover:bg-gray-300 group-focus-visible:bg-primary'
          }`}
        />
      </div>
      <FolderTree
        selectedFolderId={folderId}
        /* 폴더를 고르는 것은 「목록에서 다른 것을 보겠다」는 뜻이다 — 접힌 채로 두면 고른
           결과가 안 보인다. 시험지 선택은 그대로 살아 있고, 아래 손잡이로 되돌아간다.
           「하위 폴더 포함」은 여기서 끈다 — 다른 폴더를 보는데 앞 폴더의 조건이 남아 있으면
           화면에 없는 조건이 목록을 거른다(스위치 자체는 시험지 목록 머리줄에 있다). */
        onSelectFolder={(v) => {
          setFolderId(v);
          setIncludeChildren(false);
          setListCollapsed(false);
        }}
        onFoldersLoaded={setFolders}
        onChanged={bumpRefresh}
        reloadKey={refreshKey}
      />
    </div>
  );

  const renderPapers = (isMobileView) => (
    <PaperListPanel
      selectedId={paperId}
      onSelect={select}
      onCreate={() => go('/exam-papers/new')}
      onPreview={openPaperPreview}
      previewBusyId={previewBusyId}
      /* AI 복사 대화상자를 여는 것만으로는 아무것도 버리지 않는다 — 여기서 묻지 않는다 */
      onAiCopy={canAiCopy ? (p) => setAiCopyTarget(p) : null}
      onOpenBatch={canAiCopy ? openBatch : null}
      onMoveTo={moveTo}
      folders={folders}
      onCopy={(p) => { setRowToast(null); setRowAction({ kind: 'copy', paper: p, folderId: p.folder_id ?? null, title: '' }); }}
      onOpenAssign={openAssign}
      onDelete={(p) => { setRowToast(null); setRowAction({ kind: 'delete', paper: p }); }}
      folderId={folderId}
      includeChildren={includeChildren}
      /* 「하위 폴더 포함」 스위치는 이 목록의 머리줄이 그린다(오너 지시 2026-08-28) —
         무엇을 보는지 정하는 스위치는 그 결과가 바뀌는 자리 옆에 있어야 한다. */
      folderKidCount={folderScope.kidCount}
      folderTotalCount={folderScope.totalCount}
      onIncludeChildrenChange={setIncludeChildren}
      /* 상하로 나뉘므로 목록이 접혀도 가로 폭은 줄지 않는다 — 넓은 폭이면 언제나 표다
         (좌우로 나뉘던 때는 상세가 열리면 폭이 360~440px 로 좁아져 9컬럼이 안 들어갔다) */
      layout={isMobileView ? 'card' : 'table'}
      /* 넓은 폭에서는 상세와 카드 한 장을 나눠 쓴다 — 껍질은 담는 쪽이 두른다 */
      chromeless={!isMobileView}
      onHeaderHeight={isMobileView ? null : onHeaderHeight}
      /* 고른 시험지가 이 페이지 20건 밖에 있을 때의 폴백 — 목록에 있으면 목록 값이 이긴다 */
      selectedPaper={paper != null && String(paper.paper_id ?? paper.id) === String(paperId) ? paper : null}
      reloadKey={refreshKey}
      routeKey={location.pathname}
      /* 머리줄이 「시험지 › 고른 것」 을 말하고, 그 줄이 곧 펼침/접힘 스위치다 */
      selectedTitle={detailOpen ? detailTitle() : null}
      collapsed={detailOpen && listCollapsed}
      onToggleCollapse={detailOpen ? toggleList : null}
    />
  );

  const renderDetailBody = () => {
    // 배정(채점)은 이 자리를 쓰지 않는다 — 창으로 뜬다(아래 AssignmentGradeModal)
    if (batchId != null) {
      return (
        <BatchPanel
          key={`b-${batchId}`}
          batchId={batchId}
          onDirtyChange={markDirty}
          onBack={() => leaveDetail('/exam-papers')}
          onAssembled={(newPaperId) => {
            markDirty(false);
            bumpRefresh();
            if (newPaperId != null) navigate(`/exam-papers/paper/${newPaperId}`);
            else navigate('/exam-papers');
          }}
          onDiscarded={(discardedId) => {
            say({ type: 'info', text: `AI변형 시험지를 폐기했습니다 (#${discardedId}).` });
            // 응답을 기다리는 사이 다른 화면으로 떠났다 — 그 화면을 옮기지 않고 「진행중」 줄만 다시 읽힌다.
            // 미저장 깃발도 건드리지 않는다. 그 깃발은 지금 화면의 것이다.
            if (String(batchIdRef.current) !== String(discardedId)) {
              bumpRefresh();
              return;
            }
            markDirty(false);
            // 목록의 「진행중」 줄은 경로가 바뀔 때 다시 읽으므로(routeKey) 따로 새로 읽히지 않는다
            leaveDetail('/exam-papers');
          }}
          onDiscardFailed={(text) => say({ type: 'error', text })}
        />
      );
    }
    if (isNew) {
      // 새 시험지는 3열 제작 모달이 맡는다(오너 결정) — 이 자리는 모달을 닫았을 때의 되돌아갈 곳이다
      return (
        <div className="flex flex-col items-center justify-center gap-3 p-10 text-center">
          <FileText className="size-10 text-gray-300" />
          <p className="text-sm text-gray-500">새 시험지는 제작 창에서 만듭니다.</p>
          <Button size="sm" onClick={() => setComposeFor('new')}>제작 창 열기</Button>
          <button type="button" onClick={() => navigate('/exam-papers')} className="text-xs text-gray-400 underline">
            목록으로
          </button>
        </div>
      );
    }
    // 옛 시험지가 남아 있어도 스켈레톤을 띄운다 — nullness 로 재면 A 의 내용이 B 자리에 보인다
    if (paper == null || String(paper.paper_id ?? paper.id ?? paperId) !== String(paperId)) {
      if (error) {
        return (
          <div className="space-y-2 p-4">
            <p role="alert" className="text-sm text-danger">{error}</p>
            <button type="button" onClick={loadPaper} className="text-xs text-primary underline">다시 시도</button>
          </div>
        );
      }
      return (
        <div className="space-y-2 p-1">
          <div className="h-6 w-40 rounded bg-gray-100 motion-safe:animate-pulse" />
          <div className="h-32 w-full rounded-xl bg-gray-100 motion-safe:animate-pulse" />
        </div>
      );
    }
    return (
      <>
        <div className={tab === 'items' ? '' : 'hidden'} role="tabpanel" id="ep-panel-items" aria-labelledby="ep-tab-items">
          <PaperItemsEditor
            /* 키에 revision 을 넣으면 저장 성공이 곧 remount 라, 방금 뜬 "저장했습니다"가 함께
               사라진다. 판이 실제로 바뀌었는지는 편집기가 스스로 본다(seededKey). */
            key={`i-${paperId}`}
            paper={paper}
            /* 같은 시험지를 두 곳에서 고치면 한쪽 저장이 다른 쪽을 덮는다 — 넘어가기 전에 가드를 지난다 */
            onOpenCompose={() => { if (confirmDiscard()) setComposeFor(paper); }}
            onDirtyChange={markDirty}
            onSaved={() => { markDirty(false); bumpRefresh(); }}
            onDeleted={() => { markDirty(false); bumpRefresh(); navigate('/exam-papers'); }}
          />
        </div>
        <div className={tab === 'assign' ? '' : 'hidden'} role="tabpanel" id="ep-panel-assign" aria-labelledby="ep-tab-assign">
          <AssignPanel
            key={`a-${paperId}`}
            paperId={paperId}
            paper={paper}
            paperDirty={paperDirty}
            onDirtyChange={markDirty}
            /* 전체 화면으로 갈아 끼우지 않고 결과표 위에 창을 얹는다 — 여러 학생을 잇달아
               채점할 때 표가 사라지지 않는다. 학생 학습관리에서 오는 길은 종전대로 전체 화면이다. */
            onOpenAssignment={openGrade}
            refreshToken={assignRefresh}
          />
        </div>
        <div className={tab === 'history' ? '' : 'hidden'} role="tabpanel" id="ep-panel-history" aria-labelledby="ep-tab-history">
          <RevisionPanel
            /* 되돌리기 성공 메시지가 remount 로 지워지지 않게 — 목록은 패널이 스스로 다시 읽는다.
               문항 저장·구성 창 저장 뒤에도 같은 신호(refreshKey)로 따라 읽는다 */
            key={`h-${paperId}`}
            paperId={paperId}
            currentRevision={paper.revision}
            reloadKey={refreshKey}
            onBeforeRevert={confirmDiscard}
            onReverted={() => { markDirty(false); bumpRefresh(); }}
          />
        </div>
      </>
    );
  };

  const detailTitle = () => {
    // 배정은 여기 오지 않는다 — 채점은 창으로 뜨고 상세 칸을 쓰지 않는다(2026-09-06).
    // 제목이 필요한 자리는 창이 스스로 갖는다(AssignmentPanel 머리줄의 학생명·시험지명).
    if (batchId != null) return 'AI 복사 배치';
    if (isNew) return '새 시험지';
    // 「지금 들고 있는 상세가 이 paperId 의 것인가」는 위 paperIsMine 한 곳에서만 잰다 —
    // 같은 판정을 두 벌 두면 한쪽만 고쳐졌을 때 제목과 경고가 서로 다른 시험지를 말한다
    return (paperIsMine && paper.title) || `#${paperId}`;
  };

  /**
   * stacked=true 면 목록과 **한 장의 카드**를 위아래로 나눠 쓴다 — 껍질(면·모서리·그림자)은
   * 담는 쪽이 두르므로 여기서는 벗는다. 두 장으로 두면 카드가 둘로 보여, 아래가 위에서
   * 열린 것이 아니라 따로 뜬 것처럼 읽힌다.
   *
   * 「위에서 고른 그것」임을 말하는 것은 그래서 색이 아니라 자리다 — 같은 면 안에서
   * 선 하나로 갈린다. 어느 행에서 왔는지는 표의 선택 행 초록 띠와 머리줄의
   * 「시험지 › 이름」이 말한다.
   */
  /**
   * 접힌 상세의 손잡이 — 목록을 펴면 아래가 0 이 되어 보던 시험지가 통째로 사라진다.
   * 그러면 폴더를 한 번 눌렀을 뿐인데 하던 일이 없어진 것처럼 보인다(오너 지적).
   * 그래서 한 줄만 남겨 두고, 누르면 그 자리에서 다시 펴진다.
   * 높이(44px)는 아래 DETAIL_BAR_H 와 짝이다 — 한쪽만 바꾸면 목록이 그만큼 어긋난다.
   */
  const renderDetailBar = () => (
    <button
      type="button"
      onClick={() => setListCollapsed(true)}
      title="이 시험지를 다시 펼칩니다"
      className="flex h-11 w-full items-center gap-1 px-3 text-left transition-colors hover:bg-gray-50"
    >
      <FileText aria-hidden="true" className="size-4 shrink-0 text-primary" />
      <span className="shrink-0 text-sm font-bold text-gray-800">시험지</span>
      <ChevronRight aria-hidden="true" className="size-3 shrink-0 text-gray-300" />
      <span className="min-w-0 truncate text-sm">
        <span className="font-bold text-primary">{detailTitle()}</span>
        {paperId != null && paper != null && (
          <span className="ml-2 text-xs text-gray-500">{paperHeadMeta(paper)}</span>
        )}
      </span>
      <ChevronUp aria-hidden="true" className="ml-auto size-3.5 shrink-0 text-gray-400" />
    </button>
  );

  const renderDetail = (stacked = false) => (
    <div
      className={`flex h-full min-h-0 flex-col p-4 ${
        stacked ? '' : 'rounded-3xl bg-white shadow-lg'
      }`}
    >
      {/* 머리 행 — 좌(제목) · 가운데(탭) · 우(닫기) 세 칸.
          좌우를 같은 flex-1 로 두어야 탭이 행의 정중앙에 선다. 제목이 길면 왼쪽 칸 안에서 잘린다.
          (ml-auto 로 밀면 탭 위치가 제목 길이에 따라 흔들린다.) */}
      <div className="mb-3 flex shrink-0 items-center gap-2 border-b pb-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {batchId != null
            ? <Sparkles className="size-4 shrink-0 text-violet-500" />
            : <FileText className="size-4 shrink-0 text-primary" />}
          <span className="min-w-0 truncate text-sm font-bold text-gray-800">{detailTitle()}</span>
          {loading && <span className="shrink-0 text-[11px] text-gray-400">읽는 중…</span>}
        </div>
        {paperId != null && !isNew && (
          // min-w-0 + 가로 스크롤 — overflow-hidden 만 두면 좁은 폭에서 마지막 탭이 잘려 못 누른다
          <div className="flex min-w-0 shrink-0 overflow-x-auto rounded-full bg-gray-100 text-xs" role="tablist" aria-label="시험지 상세 보기">
            {TABS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                id={`ep-tab-${key}`}
                aria-controls={`ep-panel-${key}`}
                aria-selected={tab === key}
                onClick={() => goTab(key)}
                className={`whitespace-nowrap px-3 py-1 transition-colors ${
                  tab === key ? 'bg-primary font-medium text-white' : 'text-gray-500 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-1 justify-end">
          <button
            type="button"
            onClick={closeDetail}
            className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="size-4" />
            닫기
          </button>
        </div>
      </div>
      {/* 삭제된 문항 경고(계약 §1-3) — 머리 행 바로 아래, 본문 스크롤 **밖**에 둔다.
          안에 두면 문항 목록을 내리는 순간 사라지는데, 그때가 바로 배포를 누르러 가는 길이다.
          색은 형제 화면(AssignmentPanel 의 미저장 채점 경고)과 같은 amber 한 벌을 그대로 쓴다. */}
      {deletedItems > 0 && (
        <div className="mb-3 flex shrink-0 items-start gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
          <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span>
            삭제된 문항 <b>{deletedItems}개</b>가 들어 있습니다 — 이 상태로는 <b>배포·복사·되돌리기가 막힙니다.</b>
            [문항 편집]에서 해당 문항을 빼고 저장하세요.
          </span>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">{renderDetailBody()}</div>
    </div>
  );

  /* 문제 번호 링크(ProblemIdLink)가 이 화면을 떠나기 전에 물어볼 수 있게 가드를 내린다 —
     문항 편집·채점·검수의 미저장 편집은 행을 눌러 옮길 때든 번호를 눌러 나갈 때든 같은 규칙으로
     지켜져야 한다. 시험지 구성 창은 자기 편집을 아는 가드를 안쪽에서 한 겹 더 덮는다. */
  return (
    <ProblemNavGuard confirm={confirmDiscard}>
      <BypassLayout
        renderContent={({ isMobileView }) => {
          if (isMobileView) {
            return (
              <div
                className="flex flex-1 flex-col"
                style={{ width: 'calc(100% - 28.8px)', height: 'calc(100% - 28.8px)', margin: '14.4px' }}
              >
                {/* 목록은 감추기만 한다 — 언마운트하면 돌아왔을 때 페이지·범위가 사라진다 */}
                <div className={`flex min-h-0 flex-1 flex-col ${detailOpen ? 'hidden' : ''}`}>{renderPapers(true)}</div>
                {detailOpen && <div className="flex min-h-0 flex-1 flex-col">{renderDetail()}</div>}
              </div>
            );
          }
          /**
           * 넓은 폭은 위아래로 나눈다(오너 지시 2026-08-25). 위에서 고른 것이 아래에 열리는
           * 관계라, 좌우로 벌리는 것보다 위아래로 쌓는 편이 그 방향을 그대로 말한다.
           *
           * 높이만 애니메이션한다 — 위가 줄어드는 만큼 아래가 자라야 「하나의 동작」으로 읽힌다.
           * 그래서 위에 flex-shrink:0 + height 를 주고 아래는 flex-1 로 남은 만큼 갖게 했다.
           * (둘 다 height 를 %로 주면 gap 때문에 합이 100%를 넘어 마지막에 튄다.)
           */
          // 두 칸이 서로의 자리를 주고받되 **어느 쪽도 완전히 사라지지 않는다.**
          // 상세를 보면 목록은 머리줄 한 줄(실측 + 카드 여백 24)만, 목록을 보면 상세는 손잡이 한 줄만.
          const DETAIL_BAR_H = 44; // renderDetailBar 의 h-11 과 같은 값이어야 한다
          const listH = !detailOpen
            ? '100%'
            : listCollapsed
              ? `${Math.round(headH) + 24}px`
              : `calc(100% - ${DETAIL_BAR_H}px)`;
          return (
            <div className="flex h-[calc(100vh-6rem)] w-full gap-3">
              {/* 1열 — 폴더. 세로 전체를 쓰고 이 분할에 끼지 않는다 */}
              {renderFolders()}

              {/* 카드는 한 장이고, 나뉘는 것은 그 안이다. 껍질을 여기서 두르므로
                  안의 둘(목록·상세)은 chromeless·stacked 로 껍질을 벗는다. */}
              <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
                <div
                  className="min-h-0 shrink-0"
                  style={{
                    height: listH,
                    transition: 'height 320ms cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {renderPapers(false)}
                </div>
                {/* 펼치면 위가 100% 를 먹어 이 칸은 0 이 된다 — 언마운트하지 않으므로 탭·스크롤이 남는다.
                    가르는 선은 아래 칸의 위 테두리다. 높이가 0 이어도 테두리는 1px 로 남으므로,
                    접혀 있을 때만 색을 준다(투명으로 두면 선이 사라진다). */}
                {detailOpen && (
                  <div
                    className={`min-h-0 flex-1 overflow-hidden border-t transition-colors duration-300 ${
                      listCollapsed ? 'border-gray-200' : 'border-transparent'
                    }`}
                  >
                    {listCollapsed ? renderDetail(true) : renderDetailBar()}
                  </div>
                )}
              </div>
            </div>
          );
        }}
      />
      {printData && (
        <Suspense fallback={null}>
          <PrintPreviewModal
            isOpen
            onClose={() => setPrintData(null)}
            testData={printData}
            isMobileView={window.innerWidth < 768}
          />
        </Suspense>
      )}
      {/* 3열 제작 창 — 새 시험지와 기존 시험지 편집을 같은 창이 맡는다 */}
      {composeFor && (
        <PaperComposeModal
          isOpen
          paper={composeFor === 'new' ? null : composeFor}
          onClose={() => {
            setComposeFor(null);
            if (isNew) navigate('/exam-papers', { replace: true });
          }}
          onSaved={(newPaperId) => {
            const wasNew = composeFor === 'new';
            setComposeFor(null);
            markDirty(false);
            bumpRefresh();
            if (wasNew) {
              // replace — 뒤로 가서 빈 폼이 다시 나오면 같은 시험지를 두 번 만들기 쉽다
              navigate(newPaperId != null ? `/exam-papers/paper/${newPaperId}` : '/exam-papers', { replace: true });
            }
          }}
          onAssignRequested={(savedPaperId) => {
            setComposeFor(null);
            bumpRefresh();
            if (savedPaperId != null) {
              // 탭을 주소에 실어 한 번에 간다 — 전에는 navigate 뒤 setTab 이었는데, 이제 주소가
              // 정본이라 두 줄로 나누면 [문항 편집]으로 갔다가 [학생 출제]로 튀는 두 칸이 된다
              navigate(`/exam-papers/paper/${savedPaperId}?tab=assign`, { replace: isNew });
            }
          }}
        />
      )}

      {/* 채점 창 — 결과표의 [결과]가 연다. 주소(`?tab=assign&grade=<aid>`)가 열림을 쥐므로
          새로고침해도 같은 배포본이 다시 열리고, 뒤로가기로 창만 닫힌다.
          학생 학습관리에서 오는 `/exam-papers/assignment/:aid` 는 종전대로 전체 화면이다. */}
      {openGradeId != null && (
        <AssignmentGradeModal
          key={`g-${openGradeId}`}
          assignmentId={openGradeId}
          isOpen
          onClose={closeGradeModal}
          onChanged={handleGraded}
          onDirtyChange={markDirty}
        />
      )}

      {/* 결과 띠 — 이동·복사·삭제의 성공. 대화상자가 닫힌 뒤에도 남는 유일한 설명이다.
          위 확인 창과 같은 이유로 body 로 내보낸다 — 페이지 안에 두면 fixed 가 부모 상자에
          갇혀 화면 아래가 아니라 엉뚱한 자리에 서거나 높이 0 으로 접힌다 */}
      {flash && createPortal(
        (
        <div
          className="pointer-events-none fixed inset-x-0 bottom-6 flex justify-center px-4"
          style={{ zIndex: 'var(--z-toast)' }}
        >
          <p
            role={flash.type === 'error' ? 'alert' : 'status'}
            aria-live={flash.type === 'error' ? 'assertive' : 'polite'}
            className={`pointer-events-auto max-w-full truncate rounded-full px-4 py-2 text-xs font-medium shadow-2xl ${
              flash.type === 'error' ? 'bg-danger text-white' : 'bg-gray-900 text-white'
            }`}
          >
            {flash.text}
          </p>
        </div>
        ),
        document.body,
      )}

      {/*
        행 액션 확인 — 복사·삭제는 결과가 서로 달라 같은 문구로 물으면 안 된다.

        ⚠ 공용 Modal 을 쓴다. 손으로 만든 `fixed inset-0` 는 이 앱에서 **뜨지 않는다** —
        App.css 의 `* { transform-style: preserve-3d }` 가 모든 요소를 고정 요소의 기준 상자로
        만들어, fixed 가 화면이 아니라 부모 상자에 갇힌다(실측: 1200×800 이어야 할 덮개가
        496×0 이 된다 = 높이 0 = 안 보인다). 공용 Modal 은 createPortal 로 body 에 내보내서
        그 덫을 넘는다. 이 화면의 다른 창들이 전부 뜨는 이유가 그것이다.
      */}
      {rowAction && (
        <Modal
          isOpen
          onClose={() => { setRowAction(null); setRowToast(null); }}
          title={rowAction.kind === 'copy' ? '시험지 복사' : '시험지 삭제'}
          size="md"
          closeOnBackdropClick={!rowBusy}
          closeOnEsc={!rowBusy}
          zIndex={9999975}
          bodyClassName="space-y-3"
          footer={
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => { setRowAction(null); setRowToast(null); }} disabled={rowBusy}>
                취소
              </Button>
              <Button
                size="sm"
                variant={rowAction.kind === 'delete' ? 'danger' : 'primary'}
                onClick={runRowAction}
                loading={rowBusy}
                disabled={rowBusy}
              >
                {rowAction.kind === 'copy' ? '복사' : '삭제'}
              </Button>
            </div>
          }
        >
          <div className="space-y-3">
            <p className="truncate text-xs text-gray-500">{rowAction.paper.title || `#${rowAction.paper.paper_id ?? rowAction.paper.id}`}</p>

            {rowAction.kind === 'copy' && (
              <label className="block text-xs text-gray-600">
                <span className="mb-1 block font-medium">사본을 둘 폴더</span>
                <FolderPickerInline
                  folders={folders}
                  value={rowAction.folderId}
                  onChange={(v) => setRowAction((r) => ({ ...r, folderId: v }))}
                  includeUnfiled
                  disabled={rowBusy}
                />
              </label>
            )}
            {rowAction.kind === 'copy' && (
              <>
                <label className="block text-xs text-gray-600">
                  <span className="mb-1 block font-medium">사본 제목(비우면 &quot;{'{원본}'} (사본)&quot;)</span>
                  <input
                    type="text"
                    value={rowAction.title}
                    onChange={(e) => setRowAction((r) => ({ ...r, title: e.target.value }))}
                    disabled={rowBusy}
                    className="w-full rounded-lg border px-2 py-1.5 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50"
                  />
                </label>
                <p className="rounded-xl bg-gray-50 p-2 text-[11px] leading-relaxed text-gray-600">
                  사본은 원본과 <b>연결되지 않은 독립 시험지</b>입니다 — 한쪽을 고쳐도 다른 쪽은 그대로입니다.
                  (AI 복사와 달리 문항을 새로 만들지 않고 지금 문항을 그대로 복제합니다.)
                </p>
              </>
            )}
            {rowAction.kind === 'delete' && (
              <>
                <p className="rounded-xl bg-red-50 p-2 text-[11px] leading-relaxed text-red-700">
                  이 시험지를 삭제합니다. <b>폴더 삭제와 달리 시험지 자체가 사라집니다.</b>
                  목록에서는 이 시험지를 다시 열 수 없습니다.
                </p>
                {/* 누르기 전에 말한다 — 계약 §10 은 "배정 수>0 이면 버튼 비활성"을 권하지만
                    목록 요약에 배정 수가 오지 않아 **행에서는 알 수 없다.** 그래서 막는 것은
                    서버(409 has_assignments)이고, 화면이 할 수 있는 것은 미리 알리는 것뿐이다. */}
                {/* role="alert" 를 붙이지 않는다 — 창이 열릴 때부터 있는 글이라 「방금 생긴 소식」이
                    아니고, 붙이면 대화상자 본문을 읽기 전에 이 문장이 먼저 끼어들어 두 번 읽힌다 */}
                <p className="flex items-start gap-1.5 rounded-xl border border-amber-200 bg-amber-50 p-2 text-[11px] leading-relaxed text-amber-900">
                  <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                  <span>
                    <b>한 번이라도 배포된 시험지는 삭제할 수 없습니다.</b>
                    배포본은 마감분까지 그대로 남아야 하기 때문입니다 — 이 경우 [삭제]를 눌러도
                    배포 건수와 함께 거절 안내가 뜹니다.
                  </span>
                </p>
              </>
            )}

            <p
              role={rowToast?.type === 'error' ? 'alert' : 'status'}
              aria-live={rowToast?.type === 'error' ? 'assertive' : 'polite'}
              className={`text-xs ${rowToast ? (rowToast.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
            >
              {rowToast?.text || ''}
            </p>
          </div>
        </Modal>
      )}

      {/* 닫으면 언마운트한다 — isOpen 만 내리면 대화상자 바깥의 상태가 남는다 */}
      {aiCopyTarget && (
        <AiCopyModal
          isOpen
          paper={aiCopyTarget}
          onClose={() => setAiCopyTarget(null)}
          onStarted={(newBatchId) => {
            setAiCopyTarget(null);
            // go() 를 거친다 — 여기만 navigate 를 직접 불러 [문항]의 미저장 편집을 말없이 버렸다
            if (newBatchId != null) go(`/exam-papers/batch/${newBatchId}`);
          }}
        />
      )}
    </ProblemNavGuard>
  );
}

export default ExamPapers;
